import {
  AfterViewInit,
  Component,
  HostListener,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { NavigationEnd, Router, RoutesRecognized } from '@angular/router';
import { Store } from '@ngxs/store';
import { Observable, Subject } from 'rxjs';
import {
  filter,
  map,
  pairwise,
  switchMap,
  takeUntil,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import {
  QuoteActions,
  QuoteState,
  QuoteDataService,
} from '@nibtravel/shared/data-access/quote';
import { UnderwriterAction } from '@nibtravel/shared/data-access/underwriter';
import { AppConfigState } from '@nibtravel/shared/util/app-config';
import { LoggerService } from '@nibtravel/shared/util/logger';
import { ContentfulService } from '@nibtravel/shared/data-access/contentful';
import { FooterHelper } from '@nibtravel/shared/ui/components/footer';
import { ITheme } from '@nibtravel/shared/ui/theme';
import { IQuotePriceSummary } from '@nibtravel/shared/util/models';

@Component({
  selector: 'nibtravel-app',
  templateUrl: './app.component.html',
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
  destroy$ = new Subject();
  themeConfig!: ITheme;
  footerConfig$!: Observable<any>;

  constructor(
    private store: Store,
    private router: Router,
    private loggerService: LoggerService,
    private contenfulService: ContentfulService,
    private _quoteDataService: QuoteDataService
  ) {}

  @HostListener('window:beforeunload', ['$event'])
  beforeUnloadHandler(): void {
    this.loggerService.flushLogs();
  }

  ngOnInit(): void {
    this.themeConfig = this.getSnapshot(AppConfigState.getThemeConfig);
    this.themeConfig.isStandalone = false;
    this.footerConfig$ = this.contenfulService
      .getFooter(this.themeConfig.brand)
      .pipe(map((entry) => FooterHelper.toFooterConfig(entry?.fields)));
  }

  ngAfterViewInit(): void {
    const brand = this.getSnapshot(AppConfigState.getBrand);
    const partner =
      this.getSnapshot(QuoteState.quoteInput)?.partnerCode ||
      this.getSnapshot(AppConfigState.partner);
    const actions: any[] = [
      new QuoteActions.SetPartnerCode(partner),
      new UnderwriterAction.LoadSummary(),
    ];

    // This is a hack until the back end has the correct handling for cancellation option
    if (brand === 'tidau') {
      const hasValue = !!this.store.selectSnapshot(QuoteState.quoteInput)
        ?.cancellationOption?.cancellationCoverLimit;
      if (!hasValue) {
        actions.push(new QuoteActions.SetDefaultCancellationOption());
      }
    }

    // Perform reset based on the route events.
    // It's a bit of an overhead but at the moment, it's the most reliable way
    this.router.events
      .pipe(
        filter((evt) => evt instanceof NavigationEnd),
        map((evt) => evt as NavigationEnd),
        tap((evt) =>
          this.store.dispatch(
            new QuoteActions.SetQuoteCurrentRoute(evt.urlAfterRedirects)
          )
        ),
        tap((evt) => {
          // if users navigate back, reset plan id to undefined.
          const url = ['/'];
          if (url.includes(evt.urlAfterRedirects)) {
            this.store.dispatch(new QuoteActions.SetPlanForQuote(undefined));
          }
        }),
        filter((evt) => {
          // TODO: Once we implement the route guards, we should reset any page
          // that is not the confirmation and redirect user to home page.
          const isPurchased = this.getSnapshot(QuoteState.isPurchased);
          return evt.url === '/' && isPurchased;
        }),
        switchMap(() => {
          return this.store.dispatch([
            new QuoteActions.ResetQuote(),
            ...actions,
          ]);
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();

    this.router.events
      .pipe(
        filter((e): e is RoutesRecognized => e instanceof RoutesRecognized),
        pairwise(),
        withLatestFrom(this._quoteDataService.getPreviousQuotePrices())
      )
      .subscribe(([[previousEvent, currentEvent], previousQuotePrices]) => {
        const { urlAfterRedirects: prevUrl } = previousEvent;
        const { urlAfterRedirects: currentUrl } = currentEvent;
        //reset plan prices if users navigate to /quote/plans from non-root page or cms page
        const isValidNavigation =
          prevUrl !== '/' &&
          currentUrl === '/quote/plans' &&
          (previousQuotePrices as IQuotePriceSummary[])?.length > 0 &&
          !prevUrl.includes('turnstile');
        if (isValidNavigation) {
          this.store.dispatch(
            new QuoteActions.ResetPlanPrices(previousQuotePrices)
          );
        }
      });

    this.store.dispatch(actions);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Abstract the store selectSnapshot to provide syntactic suger, which improves readability */
  private getSnapshot<T>(selector: (state: any, ...states: any[]) => T): T {
    return this.store.selectSnapshot<T>(selector);
  }
}
